import React, { useMemo } from 'react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '../components/ui/card';
import { Badge } from '../components/ui/badge';
import {
  BarChart, Bar, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer,
  PieChart, Pie, Cell, Legend,
  AreaChart, Area
} from 'recharts';
import { TrendingUp, Target, Award, AlertTriangle, IndianRupee } from 'lucide-react';

const COLORS = ['#0ea5e9', '#8b5cf6', '#f59e0b', '#10b981', '#ef4444', '#6366f1', '#ec4899', '#14b8a6', '#f97316', '#64748b'];

const STATUS_LABELS = {
  new: 'New', contacted: 'Contacted', qualified: 'Qualified',
  follow_up_1: 'FU 1', follow_up_2: 'FU 2', follow_up_3: 'FU 3',
  proposal: 'Proposal', won: 'Won', lost: 'Lost',
};

const WIN_PROBABILITY = {
  new: 10, contacted: 20, qualified: 40, follow_up_1: 50,
  follow_up_2: 60, follow_up_3: 70, proposal: 80, won: 100, lost: 0,
};

const formatCurrency = (val) => {
  if (!val && val !== 0) return '₹0';
  const num = Number(val);
  if (num >= 10000000) return `₹${(num / 10000000).toFixed(1)}Cr`;
  if (num >= 100000) return `₹${(num / 100000).toFixed(1)}L`;
  if (num >= 1000) return `₹${(num / 1000).toFixed(0)}K`;
  return `₹${num.toLocaleString('en-IN')}`;
};

const CustomTooltip = ({ active, payload, label }) => {
  if (!active || !payload?.length) return null;
  return (
    <div className="bg-white border rounded-lg shadow-lg p-3 text-sm">
      <p className="font-medium mb-1">{label}</p>
      {payload.map((p, i) => (
        <p key={i} style={{ color: p.color }}>
          {p.name}: {p.name.includes('Value') || p.name.includes('Revenue') ? formatCurrency(p.value) : p.value}
        </p>
      ))}
    </div>
  );
};

export default function CRMAnalytics({ leads, pipelineStats }) {
  const analytics = useMemo(() => {
    const allLeads = leads || [];
    const pipeline = pipelineStats || {};

    // --- Win/Loss by Source ---
    const sourceMap = {};
    allLeads.forEach(l => {
      const src = l.source || 'Unknown';
      if (!sourceMap[src]) sourceMap[src] = { name: src, total: 0, won: 0, lost: 0, value: 0, wonValue: 0 };
      sourceMap[src].total++;
      sourceMap[src].value += l.estimated_deal_size || 0;
      if (l.status === 'won') { sourceMap[src].won++; sourceMap[src].wonValue += l.estimated_deal_size || 0; }
      if (l.status === 'lost') sourceMap[src].lost++;
    });
    const bySource = Object.values(sourceMap).sort((a, b) => b.total - a.total);
    bySource.forEach(s => { s.conversionRate = s.total > 0 ? Math.round((s.won / s.total) * 100) : 0; });

    // --- Pipeline by Status (funnel) ---
    const byStatus = Object.entries(STATUS_LABELS).map(([key, label]) => {
      const statusLeads = allLeads.filter(l => l.status === key);
      const value = statusLeads.reduce((sum, l) => sum + (l.estimated_deal_size || 0), 0);
      return { name: label, status: key, count: statusLeads.length, value, probability: WIN_PROBABILITY[key] || 0 };
    }).filter(s => s.count > 0);

    // --- Revenue Forecast ---
    const forecast = byStatus
      .filter(s => s.status !== 'won' && s.status !== 'lost')
      .map(s => ({
        name: s.name,
        expectedRevenue: Math.round(s.value * (s.probability / 100)),
        dealValue: s.value,
        probability: s.probability,
        count: s.count,
      }));
    const totalExpectedRevenue = forecast.reduce((sum, f) => sum + f.expectedRevenue, 0);
    const totalWonRevenue = allLeads.filter(l => l.status === 'won').reduce((sum, l) => sum + (l.estimated_deal_size || 0), 0);

    // --- Win/Loss by Assignee ---
    const assigneeMap = {};
    allLeads.forEach(l => {
      const name = l.assigned_to_name || 'Unassigned';
      if (!assigneeMap[name]) assigneeMap[name] = { name, total: 0, won: 0, lost: 0, value: 0, wonValue: 0 };
      assigneeMap[name].total++;
      assigneeMap[name].value += l.estimated_deal_size || 0;
      if (l.status === 'won') { assigneeMap[name].won++; assigneeMap[name].wonValue += l.estimated_deal_size || 0; }
      if (l.status === 'lost') assigneeMap[name].lost++;
    });
    const byAssignee = Object.values(assigneeMap).sort((a, b) => b.total - a.total).slice(0, 10);
    byAssignee.forEach(a => { a.conversionRate = a.total > 0 ? Math.round((a.won / a.total) * 100) : 0; });

    // --- Deal Size Distribution ---
    const ranges = [
      { name: '< ₹1L', min: 0, max: 100000 },
      { name: '₹1-5L', min: 100000, max: 500000 },
      { name: '₹5-10L', min: 500000, max: 1000000 },
      { name: '₹10-50L', min: 1000000, max: 5000000 },
      { name: '₹50L+', min: 5000000, max: Infinity },
    ];
    const dealDistribution = ranges.map(r => {
      const inRange = allLeads.filter(l => (l.estimated_deal_size || 0) >= r.min && (l.estimated_deal_size || 0) < r.max);
      return { name: r.name, count: inRange.length, value: inRange.reduce((s, l) => s + (l.estimated_deal_size || 0), 0) };
    });

    // --- Source pie chart ---
    const sourcePie = bySource.slice(0, 7).map(s => ({ name: s.name, value: s.total }));

    return { bySource, byStatus, forecast, totalExpectedRevenue, totalWonRevenue, byAssignee, dealDistribution, sourcePie };
  }, [leads, pipelineStats]);

  return (
    <div className="space-y-6" data-testid="crm-analytics">
      {/* KPI Row */}
      <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
        <Card className="bg-blue-50/50 border-blue-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5 text-blue-600 text-xs mb-1"><Target className="w-3.5 h-3.5" />Expected Revenue</div>
            <p className="text-xl font-bold text-blue-700">{formatCurrency(analytics.totalExpectedRevenue)}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">Weighted by win probability</p>
          </CardContent>
        </Card>
        <Card className="bg-green-50/50 border-green-200">
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5 text-green-600 text-xs mb-1"><Award className="w-3.5 h-3.5" />Won Revenue</div>
            <p className="text-xl font-bold text-green-700">{formatCurrency(analytics.totalWonRevenue)}</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-1"><TrendingUp className="w-3.5 h-3.5" />Forecast Total</div>
            <p className="text-xl font-bold">{formatCurrency(analytics.totalWonRevenue + analytics.totalExpectedRevenue)}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">Won + Expected</p>
          </CardContent>
        </Card>
        <Card>
          <CardContent className="p-4">
            <div className="flex items-center gap-1.5 text-muted-foreground text-xs mb-1"><AlertTriangle className="w-3.5 h-3.5" />At Risk</div>
            <p className="text-xl font-bold">{(leads || []).filter(l => (l.days_since_status_change || 0) >= 7 && !['won','lost'].includes(l.status)).length}</p>
            <p className="text-[10px] text-muted-foreground mt-0.5">Stagnant 7+ days</p>
          </CardContent>
        </Card>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-2 gap-6">
        {/* Revenue Forecast by Stage */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Revenue Forecast by Stage</CardTitle>
            <CardDescription>Expected revenue weighted by win probability</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={analytics.forecast} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tickFormatter={(v) => formatCurrency(v)} tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="dealValue" name="Deal Value" fill="#e2e8f0" radius={[4, 4, 0, 0]} />
                <Bar dataKey="expectedRevenue" name="Expected Revenue" fill="#0ea5e9" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Win/Loss by Source */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Conversion by Source</CardTitle>
            <CardDescription>Win rate and deal value per lead source</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={analytics.bySource.slice(0, 8)} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 10 }} angle={-20} textAnchor="end" height={50} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="won" name="Won" fill="#10b981" stackId="a" radius={[0, 0, 0, 0]} />
                <Bar dataKey="lost" name="Lost" fill="#ef4444" stackId="a" radius={[4, 4, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Lead Source Distribution (Pie) */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Lead Source Distribution</CardTitle>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <PieChart>
                <Pie data={analytics.sourcePie} cx="50%" cy="50%" outerRadius={90} innerRadius={45} dataKey="value" label={({ name, percent }) => `${name} ${(percent * 100).toFixed(0)}%`} labelLine={{ strokeWidth: 1 }}>
                  {analytics.sourcePie.map((_, i) => <Cell key={i} fill={COLORS[i % COLORS.length]} />)}
                </Pie>
                <Tooltip />
              </PieChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Assignee Performance */}
        <Card>
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Team Performance</CardTitle>
            <CardDescription>Win rate by assignee</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={280}>
              <BarChart data={analytics.byAssignee} layout="vertical" margin={{ top: 5, right: 20, left: 70, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis type="number" tick={{ fontSize: 11 }} />
                <YAxis dataKey="name" type="category" tick={{ fontSize: 10 }} width={65} />
                <Tooltip content={<CustomTooltip />} />
                <Bar dataKey="won" name="Won" fill="#10b981" stackId="a" />
                <Bar dataKey="lost" name="Lost" fill="#ef4444" stackId="a" />
                <Bar dataKey="total" name="Total" fill="#e2e8f0" />
              </BarChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>

        {/* Deal Size Distribution */}
        <Card className="lg:col-span-2">
          <CardHeader className="pb-2">
            <CardTitle className="text-base">Deal Size Distribution</CardTitle>
            <CardDescription>Number of leads by estimated deal size range</CardDescription>
          </CardHeader>
          <CardContent>
            <ResponsiveContainer width="100%" height={220}>
              <AreaChart data={analytics.dealDistribution} margin={{ top: 5, right: 20, left: 10, bottom: 5 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#f1f5f9" />
                <XAxis dataKey="name" tick={{ fontSize: 11 }} />
                <YAxis tick={{ fontSize: 11 }} />
                <Tooltip content={<CustomTooltip />} />
                <Area type="monotone" dataKey="count" name="Leads" stroke="#8b5cf6" fill="#8b5cf6" fillOpacity={0.15} />
              </AreaChart>
            </ResponsiveContainer>
          </CardContent>
        </Card>
      </div>

      {/* Source Conversion Table */}
      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base">Source Performance Summary</CardTitle>
        </CardHeader>
        <CardContent>
          <div className="overflow-x-auto">
            <table className="w-full text-sm">
              <thead>
                <tr className="border-b text-muted-foreground">
                  <th className="text-left py-2 px-3">Source</th>
                  <th className="text-right py-2 px-3">Total</th>
                  <th className="text-right py-2 px-3">Won</th>
                  <th className="text-right py-2 px-3">Lost</th>
                  <th className="text-right py-2 px-3">Conv. Rate</th>
                  <th className="text-right py-2 px-3">Pipeline Value</th>
                  <th className="text-right py-2 px-3">Won Value</th>
                </tr>
              </thead>
              <tbody>
                {analytics.bySource.map(s => (
                  <tr key={s.name} className="border-b hover:bg-muted/50">
                    <td className="py-2 px-3 font-medium">{s.name}</td>
                    <td className="text-right py-2 px-3">{s.total}</td>
                    <td className="text-right py-2 px-3 text-green-600">{s.won}</td>
                    <td className="text-right py-2 px-3 text-red-600">{s.lost}</td>
                    <td className="text-right py-2 px-3">
                      <Badge variant={s.conversionRate >= 30 ? 'default' : 'outline'} className={s.conversionRate >= 30 ? 'bg-green-600' : ''}>
                        {s.conversionRate}%
                      </Badge>
                    </td>
                    <td className="text-right py-2 px-3">{formatCurrency(s.value)}</td>
                    <td className="text-right py-2 px-3 text-green-700 font-medium">{formatCurrency(s.wonValue)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </CardContent>
      </Card>
    </div>
  );
}
